import React, { useEffect, useRef, useState } from "react";

import { getBusinessPayrolls } from "api/business-user-management";
import {
  IBusinessPayrollsResult,
  IGetBusinessPayrollsPayload,
  PayrollStatusEnum,
} from "api/business-user-management/types";
import { useParams, useSearchParams } from "react-router-dom";
import {
  capitalizeFirstLetter,
  formatDateToDDMMMYYYY,
  handleAmountFormat,
  handleTruncateAmount,
} from "utils";
import { ONE_NAIRA_IN_KOBO } from "utils/constants";

import { customErrorToast } from "ui/custom-toast";
import { DEFAULT_PAGINATION_DATA, Pagination } from "ui/pagination";
import { Status } from "ui/status";
import { StatusUiColorEnum, TStatus } from "ui/status/types";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { TSelectedTableRowIds } from "ui/table/types";

import { BusinessPayrollsTableHeaderCTAs } from "./BusinessPayrollsTableHeaderCTAs";
import { DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS, columns } from "./const";

const payrollStatusToGeneralStatusMap: Record<PayrollStatusEnum, TStatus> = {
  completed: StatusUiColorEnum.PURPLE,
  rejected: StatusUiColorEnum.ERROR,
  processing: StatusUiColorEnum.WARNING,
  draft: StatusUiColorEnum.DISABLED,
  approved: StatusUiColorEnum.SUCCESS,
  "in-review": StatusUiColorEnum.WARNING,
};

export const BusinessPayrollsTable = () => {
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const status = searchParams.get("status");
  const search = searchParams.get("search");
  const filterRetryCount = searchParams.get("filterRetryCount");
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [businessPayrollsResult, setBusinessPayrollsResult] =
    useState<IBusinessPayrollsResult>({
      data: [],
      ...DEFAULT_PAGINATION_DATA,
    });
  const [selectedBusinessPayrollIds, setSelectedBusinessPayrollIds] =
    useState<TSelectedTableRowIds>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleGetBusinessPayrollsData = async (
    payload: IGetBusinessPayrollsPayload,
    others: {
      isRefetching: boolean;
    }
  ) => {
    const { isRefetching } = others;
    if (!isRefetching) {
      setIsLoading(true);
    }

    try {
      const { data, error, message } = await getBusinessPayrolls(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        setBusinessPayrollsResult(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    }

    if (!isRefetching) {
      setIsLoading(false);
    }
  };

  const formattedData = businessPayrollsResult.data.map((payroll) => {
    return {
      "Payroll Date": (
        <p className="text-sm text-[#667085]">
          {payroll?.createdAt
            ? formatDateToDDMMMYYYY(payroll?.createdAt)
            : "N/A"}
        </p>
      ),
      "Payroll Name": (
        <p className="text-sm text-[#101828]">{payroll?.name || "N/A"}</p>
      ),
      "Payroll Amount": (
        <p className="text-sm text-[#101828]">
          {payroll?.transaction_metrics?.total_net_pay
            ? handleAmountFormat({
                value: String(
                  handleTruncateAmount(
                    payroll?.transaction_metrics?.total_net_pay
                  )
                ),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })
            : "N/A"}
        </p>
      ),
      Employees: (
        <p className="text-sm text-[#667085]">
          {payroll?.transaction_metrics?.number_of_employees || "N/A"}
        </p>
      ),
      Fee: (
        <p className="text-sm text-[#667085]">
          {payroll?.transaction_metrics?.total_fee
            ? handleAmountFormat({
                value: String(
                  handleTruncateAmount(payroll?.transaction_metrics?.total_fee)
                ),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })
            : "N/A"}
        </p>
      ),
      "Tax Remittance": (
        <p className="text-sm text-[#667085]">
          {payroll?.transaction_metrics?.total_paye_tax
            ? handleAmountFormat({
                value: String(
                  payroll?.transaction_metrics?.total_paye_tax /
                    ONE_NAIRA_IN_KOBO
                ),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })
            : "N/A"}
        </p>
      ),
      "Pension Remittance": (
        <p className="text-sm text-[#667085]">
          {payroll?.transaction_metrics?.total_pension
            ? handleAmountFormat({
                value: String(
                  payroll?.transaction_metrics?.total_pension /
                    ONE_NAIRA_IN_KOBO
                ),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })
            : "N/A"}
        </p>
      ),
      Status: payroll?.status ? (
        <Status
          status={payrollStatusToGeneralStatusMap[payroll?.status]}
          text={capitalizeFirstLetter(payroll?.status)}
          className="w-fit"
        />
      ) : (
        "N/A"
      ),
      id: payroll?.id,
    };
  });

  const { data: _, ...paginationData } = businessPayrollsResult;

  useEffect(() => {
    if (!businessId) return;
    const payload: IGetBusinessPayrollsPayload = {
      businessId,
      page: Number(page || DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS.page),
      limit: Number(limit || DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS.limit),
    };

    if (search) {
      payload["search"] = search;
    }

    if (startDate) {
      payload["startDate"] = startDate;
    }

    if (endDate) {
      payload["endDate"] = endDate;
    }

    if (status) {
      const payrollStatus = status.split(",");
      if (!payrollStatus.includes("all")) {
        payload["status"] = payrollStatus as PayrollStatusEnum[];
      }
    }

    void handleGetBusinessPayrollsData(payload, {
      isRefetching: false,
    });
  }, [
    businessId,
    page,
    limit,
    startDate,
    endDate,
    status,
    search,
    filterRetryCount,
  ]);

  // useEffect(
  //   () => console.log("businessPayrollsResult: ", businessPayrollsResult),
  //   [businessPayrollsResult]
  // );

  return (
    <TableWrapper className="!p-0">
      <BusinessPayrollsTableHeaderCTAs
        handleGetBusinessPayrollsData={handleGetBusinessPayrollsData}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        setIsRefreshing={setIsRefreshing}
      />
      <Table
        columns={columns}
        data={formattedData}
        isLoading={isLoading && !isRefreshing}
        hasCheckbox
        selectedTableRowIds={selectedBusinessPayrollIds}
        setSelectedTableRowIds={setSelectedBusinessPayrollIds}
        tableRef={tableRef}
      />
      <Pagination
        paginationData={paginationData}
        data={businessPayrollsResult?.data || []}
        defaultPaginationParams={DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS}
        tableRef={tableRef}
        isLoadingTable={isLoading}
      />
    </TableWrapper>
  );
};
